import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { BlockedContactTargetType } from "@/core";
import {
  useBlockedContacts,
  useRemoveBlockedContact,
  useUpsertBlockedContact,
} from "@/hooks";
import { useUserStore } from "@/stores/user";
import { Plus, Trash2, X } from "lucide-react";
import { useState } from "react";

/** Domain entries are stored with a leading "@"; mailbox entries never are. */
function isDomainEntry(key: string): boolean {
  return key.startsWith("@");
}

export default function BlockedContactsPage() {
  const isAdmin = useUserStore((state) => state.isAdmin());

  const blockedContactsQuery = useBlockedContacts();
  const upsertMutation = useUpsertBlockedContact();
  const removeMutation = useRemoveBlockedContact();

  const [isAdding, setIsAdding] = useState(false);
  const [formType, setFormType] = useState<BlockedContactTargetType>("email");
  const [formValue, setFormValue] = useState("");
  const [formReason, setFormReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingRemovalId, setPendingRemovalId] = useState<string | null>(null);

  // The nav hides this page for non-admins, but the route is still reachable by
  // URL and the Firestore rules would reject the read with a raw error.
  if (!isAdmin) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-semibold">Blocked Contacts</h1>
        <p className="text-sm text-muted-foreground mt-2">
          You need admin access to view the blocklist.
        </p>
      </div>
    );
  }

  const blockedContacts = blockedContactsQuery.data ?? [];

  // Domains first: they are the broader rule, so they belong at the top where
  // someone scanning the list sees the widest blocks before the individual ones.
  const sorted = [
    ...blockedContacts.filter((entry) => isDomainEntry(entry.id)),
    ...blockedContacts.filter((entry) => !isDomainEntry(entry.id)),
  ];

  const resetForm = () => {
    setIsAdding(false);
    setFormType("email");
    setFormValue("");
    setFormReason("");
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    try {
      const result = await upsertMutation.mutateAsync({
        type: formType,
        value: formValue.trim(),
        reason: formReason.trim() || undefined,
      });

      if (result.alreadyBlocked) {
        setError(`${result.id} is already blocked.`);
        return;
      }
      resetForm();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not add the block. Please try again.",
      );
    }
  };

  const handleRemove = async (id: string) => {
    setError(null);
    try {
      await removeMutation.mutateAsync({ id });
      setPendingRemovalId(null);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not remove the block. Please try again.",
      );
    }
  };

  const canSave = formValue.trim().length > 0 && !upsertMutation.isPending;

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Blocked Contacts</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Submissions from these addresses and domains are dropped silently — no
          deal, no HubSpot record, and the submitter still sees the normal
          thank-you page.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Blocklist</CardTitle>
            <CardDescription>
              Blocking an address also covers its casing, +tag, and (on Gmail)
              dot variants. Blocking a domain covers every mailbox on it.
            </CardDescription>
          </div>
          {!isAdding && (
            <Button size="sm" onClick={() => setIsAdding(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Block
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 pl-6">Type</TableHead>
                <TableHead>Blocked</TableHead>
                <TableHead className="min-w-[22rem]">Reason</TableHead>
                <TableHead className="w-40">Added By</TableHead>
                <TableHead className="w-24 pr-6 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isAdding && (
                <TableRow className="bg-muted/30">
                  <TableCell className="pl-6 align-top pt-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Type
                      </Label>
                      <Select
                        value={formType}
                        onValueChange={(value) => {
                          setFormType(value as BlockedContactTargetType);
                          setError(null);
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="domain">Domain</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="align-top pt-4" colSpan={2}>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {formType === "domain"
                          ? "Domain (e.g. spammer.com)"
                          : "Email address"}
                      </Label>
                      <Input
                        value={formValue}
                        onChange={(event) => setFormValue(event.target.value)}
                        placeholder={
                          formType === "domain"
                            ? "spammer.com"
                            : "someone@example.com"
                        }
                        autoFocus
                      />
                      {formType === "domain" && (
                        <p className="text-xs text-muted-foreground">
                          Blocks every address on this domain.
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="align-top pt-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Reason
                      </Label>
                      <Input
                        value={formReason}
                        onChange={(event) => setFormReason(event.target.value)}
                        placeholder="client request"
                      />
                    </div>
                  </TableCell>
                  <TableCell className="pr-6 align-top pt-4">
                    <div className="flex flex-col gap-1 items-end mt-5">
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={!canSave}
                        loading={upsertMutation.isPending}
                        loadingText="Blocking..."
                      >
                        Block
                      </Button>
                      <Button size="sm" variant="ghost" onClick={resetForm}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {blockedContactsQuery.isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="pl-6 text-muted-foreground text-sm py-6"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="pl-6 text-muted-foreground text-sm py-6"
                  >
                    Nobody is blocked. Add an address or domain to stop its
                    submissions.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((entry) => {
                  const isDomain = isDomainEntry(entry.id);
                  const isConfirming = pendingRemovalId === entry.id;
                  const isRemoving =
                    removeMutation.isPending &&
                    removeMutation.variables?.id === entry.id;

                  return (
                    <TableRow key={entry.id}>
                      <TableCell className="pl-6 align-top">
                        <Badge variant={isDomain ? "default" : "secondary"}>
                          {isDomain ? "Domain" : "Email"}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-top font-medium break-all">
                        {isDomain ? entry.id.slice(1) : entry.id}
                        {isDomain && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            every address on this domain
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="align-top text-sm text-muted-foreground whitespace-normal">
                        {entry.reason || "—"}
                      </TableCell>
                      <TableCell className="align-top text-sm text-muted-foreground break-all">
                        {entry.blockedBy || "—"}
                      </TableCell>
                      <TableCell className="pr-6 align-top">
                        {isConfirming ? (
                          <div className="flex flex-col gap-1 items-end">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRemove(entry.id)}
                              loading={isRemoving}
                              loadingText="Removing..."
                            >
                              Unblock
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setPendingRemovalId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPendingRemovalId(entry.id)}
                              disabled={removeMutation.isPending}
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
